import { FC, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useHorizontalScrollDirection } from '../../helpers/hooks/useHorizontalScrollDirection';
import { SlotsDictionary, TopSlot } from '../../interfaces/TopSlot';
import { sendDatebarLoadMore } from '../../services/Analytics';
import Carousel from '../Carousel/Carousel';
import { DateButton } from './DateButton';
import { LoadMoreSlots } from './LoadMoreSlots';

interface DateBarProps {
  areLoadingSlots: boolean;
  canRetrieveMore: boolean;
  dates: string[];
  highestSlot: TopSlot;
  onLoadMore: () => void;
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  slots: SlotsDictionary;
}

interface PaginatedDate {
  containsHighestSlot: boolean;
  date: string;
  dateIndex: number;
  isDisabled: boolean;
  isSelected: boolean;
  pageIndex: number;
}

type ScrollDirection = 'left' | 'right';

const ITEMS_TO_SCROLL = 7;

const DateBar: FC<DateBarProps> = ({
  slots,
  dates,
  selectedDate,
  areLoadingSlots,
  canRetrieveMore,
  highestSlot,
  onLoadMore,
  setSelectedDate,
}) => {
  const datebarRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);
  const shouldAutoNavigateToNextPage = useRef(false);
  const hasInitialized = useRef(false);

  const { t } = useTranslation();

  useHorizontalScrollDirection(datebarRef);

  const paginatedDates: PaginatedDate[] = useMemo(() => dates.map((date, index) => ({
    dateIndex: index,
    pageIndex: Math.floor(index / ITEMS_TO_SCROLL),
    date,
    isDisabled: !slots[date],
    containsHighestSlot: date === highestSlot.start.slice(0, 10),
    isSelected: selectedDate === date,
  })), [dates, selectedDate, highestSlot, slots]);

  const handleLoadMore = useCallback(() => {
    sendDatebarLoadMore();
    onLoadMore();
  }, [onLoadMore]);

  const scrollToDateIndex = useCallback((dateIndex: number) => {
    if (!datebarRef.current) return;

    const element = datebarRef.current.children[dateIndex];

    if (element) {
      const rect = element.getBoundingClientRect();
      const parentRect = datebarRef.current.getBoundingClientRect();
      datebarRef.current.scrollTo({ left: rect.left - parentRect.left - 4 + datebarRef.current.scrollLeft, behavior: 'smooth' });
    }
  }, [datebarRef]);

  const handleArrowClick = useCallback((direction: ScrollDirection): void => {
    const selectedPageIndex = paginatedDates.find((date) => date.isSelected);
    if (!datebarRef.current) return;

    const currentSelectedPageIndex = selectedPageIndex?.pageIndex ?? 0;
    const maxPageIndex = paginatedDates[paginatedDates.length - 1].pageIndex;

    let newPageIndex = currentSelectedPageIndex;

    if (direction === 'left') {
      newPageIndex = Math.max(0, currentSelectedPageIndex - 1);
    } else {
      if (currentSelectedPageIndex < maxPageIndex) {
        newPageIndex = currentSelectedPageIndex + 1;
      } else {
        if (!canRetrieveMore || shouldAutoNavigateToNextPage.current) return;
        shouldAutoNavigateToNextPage.current = true;
        handleLoadMore();
        return;
      }
    }

    // finds the first date of the page with the highest slot
    const dateWithHighestSlot = paginatedDates.find((date) => date.containsHighestSlot && date.pageIndex === newPageIndex);
    // finds the first date of the page with the highest slot
    const dateWithSlots = paginatedDates.find((date) => date.pageIndex === newPageIndex && !date.isDisabled);
    // finds the first date of the new page
    const dateOfTheSelectedWeek = paginatedDates.find((date) => date.pageIndex === newPageIndex);

    if (!dateOfTheSelectedWeek) return; // safety check

    scrollToDateIndex(dateOfTheSelectedWeek.dateIndex);

    setSelectedDate(dateWithHighestSlot?.date ?? dateWithSlots?.date ?? dateOfTheSelectedWeek.date);
  }, [paginatedDates, scrollToDateIndex, setSelectedDate, canRetrieveMore, handleLoadMore]);

  // if first week of slots are all disabled, scroll to first active one
  useEffect(() => {
    const firstEnabledPage = paginatedDates.find((date) => !date.isDisabled);
    const firstPageIndex = firstEnabledPage?.pageIndex ?? 0;

    if (firstEnabledPage && firstPageIndex > 0 && !hasInitialized.current) {
      scrollToDateIndex(firstEnabledPage.dateIndex);
    }
    hasInitialized.current = true;
  }, [paginatedDates, hasInitialized, scrollToDateIndex]);

  // if more slots are loaded by user action, scroll to next page
  useEffect(() => {
    if (!areLoadingSlots && shouldAutoNavigateToNextPage.current) {
      const currentSelectedPage = paginatedDates.find((date) => !!date.isSelected);

      if (currentSelectedPage && currentSelectedPage.pageIndex < paginatedDates[paginatedDates.length - 1].pageIndex) {
        handleArrowClick('right');
        shouldAutoNavigateToNextPage.current = false;
      }
    }
  }, [areLoadingSlots, paginatedDates, handleArrowClick]);

  const lastPossiblePage = paginatedDates.find((date) => date.pageIndex === paginatedDates[paginatedDates.length - 1].pageIndex);

  return (
    <Carousel
      carouselRef={datebarRef}
      data={paginatedDates}
      extraChild={canRetrieveMore ? (
        <LoadMoreSlots
          loading={areLoadingSlots}
          loadMoreSlots={handleLoadMore}
        />
      ) : null}
      leftArrow={{
        onClick: () => handleArrowClick('left'),
        ariaLabel: t('slots:showPreviousSlots'),
        disabled: areLoadingSlots,
      }}
      renderItem={(paginatedDay) => (
        <DateButton
          disabled={paginatedDay.isDisabled || areLoadingSlots}
          key={paginatedDay.date}
          onSelect={setSelectedDate}
          selected={paginatedDay.isSelected}
          slot={paginatedDay.date}
        />
      )}
      rightArrow={{
        onClick: () => handleArrowClick('right'),
        isLoading: areLoadingSlots,
        disabled: !canRetrieveMore && lastPossiblePage?.isSelected,
        ariaLabel: t('slots:showNextSlots'),
      }}
    />
  );
};

export default DateBar;
